import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';

const STATUS_STYLES = {
  Available:  { bgcolor: '#dbeafe', color: '#1d4ed8' },
  Assigned:   { bgcolor: '#fff3cd', color: '#92400e' },
  Completed:  { bgcolor: '#dcfce7', color: '#166534' },
  Delivered:  { bgcolor: '#dcfce7', color: '#166534' },
  DELIVERED:  { bgcolor: '#dcfce7', color: '#166534' },
  Pending:    { bgcolor: '#f3e8ff', color: '#6b21a8' },
  Cancelled:  { bgcolor: '#fee2e2', color: '#991b1b' },
};

const PAGE_SIZE = 10;

function OrderCard({ order, onView }) {
  const statusStyle = STATUS_STYLES[order.status] || { bgcolor: '#f1f5f9', color: '#475569' };
  const displayItems = (order.items || []).slice(0, 2);
  const extraItems = (order.items || []).length - 2;

  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        mb: 2,
        overflow: 'visible',
        border: '1px solid #f1f5f9',
      }}
    >
      {/* Card header: status + accepted */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Chip
          label={order.status}
          size="small"
          sx={{
            ...statusStyle,
            fontWeight: 700,
            fontSize: '0.72rem',
            border: 'none',
            height: 22,
          }}
        />
        {order.acceptedDate && (
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
            Accepted: {order.acceptedDate}
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: '#f1f5f9' }} />

      <CardContent sx={{ px: 2, pt: 1.5, pb: '12px !important' }}>
        {/* Items */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <InventoryIcon sx={{ fontSize: 16, color: '#64748b', mt: '2px', flexShrink: 0 }} />
          <Box>
            {displayItems.map((item, i) => (
              <Typography key={i} variant="body2" sx={{ color: '#1e293b', lineHeight: 1.5 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>{item.qty}×</Box>{' '}
                {item.title}
              </Typography>
            ))}
            {extraItems > 0 && (
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                +{extraItems} more item{extraItems > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#f8fafc', mb: 1.5 }} />

        {/* Pickup + Delivery row */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          {/* Pickup */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
              <StorefrontIcon sx={{ fontSize: 14, color: '#7c3aed', mt: '2px', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.05em' }}>
                  Pickup
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {order.vendor || '—'}
                </Typography>
                {order.vendorAddress && (
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.vendorAddress}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

          {/* Delivery */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
              <PersonIcon sx={{ fontSize: 14, color: '#1976d2', mt: '2px', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.05em' }}>
                  Delivery
                </Typography>
                {order.customer && (
                  <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.customer}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {order.deliveryAddress || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#f8fafc', mb: 1.5 }} />

        {/* Commission + Action row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: '#16a34a', lineHeight: 1.3 }}>
              {order.commission} Commission
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Order Value {order.value}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => onView && onView(order)}
            sx={{
              bgcolor: '#1976d2',
              color: '#fff',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2,
              py: 0.75,
              flexShrink: 0,
              '&:hover': { bgcolor: '#1565c0' },
            }}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AgentOrderMobileCards({ orders = [], onView }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visible = orders.slice(0, visibleCount);

  return (
    <Box sx={{ px: 0, pt: 1 }}>
      {visible.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
          <Typography variant="body2">No orders found</Typography>
        </Box>
      )}

      {visible.map((order) => (
        <OrderCard key={order.id} order={order} onView={onView} />
      ))}

      {visibleCount < orders.length && (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          sx={{
            borderColor: '#e2e8f0',
            color: '#475569',
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            py: 1.25,
            mb: 2,
            '&:hover': { borderColor: '#1976d2', color: '#1976d2', bgcolor: 'rgba(25,118,210,0.04)' },
          }}
        >
          Load More ({orders.length - visibleCount} remaining)
        </Button>
      )}
    </Box>
  );
}
